import * as THREE from 'three';
import { WallType, WallShapeType, JointPolygonData } from '../types/floorplannerTypes';
import { JointInfo } from './JointDetector';
import type { FloorplannerStore } from '../store/floorplannerStore';
import { createJointPolygon } from './jointPolygonGenerator';

// Lazy accessor to avoid circular dependency with floorplannerStore
let _floorplannerStore: FloorplannerStore | null = null;
const getFloorplannerStore = (): FloorplannerStore => {
  if (!_floorplannerStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniFloorplannerStore?: FloorplannerStore }).__boniFloorplannerStore;
    if (globalStore) {
      _floorplannerStore = globalStore;
    } else {
      throw new Error('FloorplannerStore not yet initialized - MultiJointRenderer called too early');
    }
  }
  return _floorplannerStore;
};

/**
 * Multi-joint rendering result
 */
export interface MultiJointRenderResult {
  success: boolean;
  error?: string;
  wallShapes: Map<string, {
    inner: WallShapeType[];
    outline1: WallShapeType[];
    outline2: WallShapeType[];
    endcaps: WallShapeType[];
  }>;
  jointPolygons?: JointPolygonData[]; // Optional field for edge-based mode
}

/**
 * Multi-Joint Renderer - Simplified for Edge-Based Connections
 *
 * This file delegates to jointPolygonGenerator.ts for gap filling.
 * Walls remain simple 4-point rectangles, and joint polygons (separate meshes) fill gaps between wall edges.
 * Outline segmentation handles outline gaps.
 *
 * @see jointPolygonGenerator.ts - Creates gap-filling polygons
 * @see JOINT_POLYGON_RENDERING.md - Detailed documentation
 * @see EDGE_CONNECTION_ARCHITECTURE.md - Edge connection system
 */
export class MultiJointRenderer {
  /**
   * Render a multi-joint by generating a joint polygon
   *
   * This method always delegates to createJointPolygon() to generate gap-filling polygons.
   * Errors are caught and logged, but return success to allow fallback.
   *
   * @param joint The joint information from JointDetector
   * @returns Multi-joint rendering results with joint polygons
   */
  renderMultiJoint(joint: JointInfo): MultiJointRenderResult {
    try {
      // Generate joint polygon from joint information
      // The joint polygon fills the gap between connected wall edges
      const jointPolygon = createJointPolygon(joint, getFloorplannerStore().wallsMap);

      // If polygon was successfully created, return it for rendering
      if (jointPolygon) {
        return {
          success: true,
          wallShapes: new Map(), // No wall shapes modified
          jointPolygons: [jointPolygon] // Joint polygon will be rendered as separate mesh
        };
      } else {
        // No polygon needed (e.g., simple connection, very small gap)
        // This is still a success - just no joint polygon to render
        return {
          success: true,
          wallShapes: new Map(),
          jointPolygons: [] // Empty array - no joint polygon to render
        };
      }
    } catch (error) {
      // Error handling: Log error but return success to allow fallback
      // This prevents blocking the rendering pipeline if joint polygon fails
      console.error('MultiJointRenderer: Error generating joint polygon', {
        jointId: joint.id,
        error: error instanceof Error ? error.message : String(error)
      });
      // Return success with empty result to allow normal wall rendering
      return {
        success: true,
        wallShapes: new Map(),
        jointPolygons: [] // Fallback to no joint polygon
      };
    }
  }

  /**
   * Clear cache for a specific joint (no-op)
   *
   * Caching is handled by WallGeometryCache instead, so this is a no-op.
   * Joint polygons are cached in WallGeometryCache.jointPolygonCache.
   * Cache key is based on joint ID.
   * Invalidated automatically when wall geometry changes.
   *
   * @param jointId The ID of the joint to clear cache for
   */
  clearJointCache(jointId: string): void {
    // Caching is handled by WallGeometryCache with automatic invalidation
  }

  /**
   * Clear all caches (no-op)
   *
   * Cache management is centralized in WallGeometryCache.
   * This method is kept for API compatibility.
   */
  clearAllCaches(): void {
    // WallGeometryCache handles all caching automatically
  }

  /**
   * Get cache statistics (returns empty stats)
   *
   * The cache is managed by WallGeometryCache, not this class.
   * Use WallGeometryCache.getStats() to get actual cache statistics.
   *
   * @returns Empty cache statistics object
   */
  getCacheStats(): { size: number; jointIds: string[] } {
    return {
      size: 0, // No cache in this class
      jointIds: [] // No joint IDs tracked in this class
    };
  }
}

// Export singleton instance
export const multiJointRenderer = new MultiJointRenderer();
